<template>
  <teleport to="body">
    <div v-if="visible" class="modal-overlay">
      <div class="modal-container">
        <!-- 1) Header fixo -->
        <div class="modal-header">
          <h6>{{ modalTitle }}</h6>
          <i class="bi bi-x-circle close-icon" @click="close" />
        </div>

        <!-- 2) Body scrollable -->
        <div class="modal-body">
          <!-- Loading ou erro -->
          <div v-if="isLoading || erro" class="d-flex flex-column align-items-center p-4">
            <LoadingSpinner />
            <p v-if="erro" class="alert alert-danger mt-3 text-center">{{ erro }}</p>
          </div>

          <!-- Conteúdo do e-mail -->
          <div v-else>
            <div v-if="!templateProcessado" class="text-center">
              Carregando template…
            </div>
            <div v-else class="ql-editor" v-safe-html="templateProcessado"></div>

            <!-- Aviso de leitura dentro do scroll -->
            <div
              v-if="readOnly"
              class="mt-3 text-muted fst-italic text-end"
              style="font-size: 0.7rem;"
            >
              E-mail enviado em {{ enviadoEm }} por {{ aniversariante.por }}
            </div>
          </div>
        </div>

        <!-- 3) Footer fixo -->
        <div class="modal-footer">
          <b-button v-if="readOnly" variant="primary" @click="close">
            Fechar
          </b-button>
          <template v-else>
            <b-button variant="secondary" @click="close">Cancelar</b-button>
            <b-button
              variant="primary"
              :disabled="isSending || !templateProcessado"
              @click="enviarEmail"
            >
              <span v-if="isSending">Enviando…</span>
              <span v-else>Enviar E-mail</span>
            </b-button>
          </template>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { renderHtmlTemplate } from '@/utils/safeHtml';
import { ref, watch, computed, getCurrentInstance } from 'vue';
import axios from '@/services/axios';
import { BButton } from 'bootstrap-vue-next';
import globalUtils from "@/utils/globalUtils.js";

const { appContext } = getCurrentInstance();
const userName = appContext.config.globalProperties.$globalState.userName;

const props = defineProps({
  visible:        Boolean,
  aniversariante: { type: Object, required: true },
  readOnly:       { type: Boolean, default: false }
});
const emit = defineEmits(['close', 'sent']);

const enviadoEm = computed(() => {
  const dt = props.aniversariante.enviado_em;
  return dt ? globalUtils.formatarData(dt) : '';
});

const isLoading = ref(false);
/* eslint-disable-next-line no-unused-vars */
const empresa = ref({ nome: '' });
const isSending = ref(false);
const templateRaw = ref('');
const templateProcessado = ref('');
const erro = ref('');

const modalTitle = computed(() => {
  const nome = props.aniversariante.nome;
  return `Feliz Aniversário, ${nome}!`;
});

const pluralMap = {
  Aluno: 'Alunos',
  'Responsável': 'Responsáveis',
  Funcionário: 'Funcionários'
};
const documentoTitulo = computed(() => {
  const tipo = props.aniversariante.tipo;
  const plural = pluralMap[tipo] || `${tipo}s`;
  return `E-mail de Feliz Aniversário - ${plural}`;
});

watch(
  () => props.visible,
  async (visible) => {
    if (visible) {
      erro.value = '';
      templateRaw.value = '';
      templateProcessado.value = '';
      isLoading.value = true;
      await carregarTemplate();
      isLoading.value = false;
    }
  },
  { immediate: true }
);

async function carregarTemplate() {
  try {
    const empRes = await axios.get(`/configuracoes/empresa`);
    empresa.value = { nome: empRes.data.nome || empRes.data.nome_fantasia };
    const res = await axios.get(`/configuracoes/documentos`);
    const docs = Array.isArray(res.data) ? res.data : Array.isArray(res.data.data) ? res.data.data : [];
    const doc = docs.find(d => d.titulo === documentoTitulo.value);
    if (!doc) {
      console.error(`Modelo não encontrado: ${documentoTitulo.value}`);
      throw new Error(`Modelo "${documentoTitulo.value}" não encontrado.`);
    }
    templateRaw.value = doc.conteudo;
    processarTemplate();
  } catch (e) {
    console.error('Erro carregarTemplate:', e);
    erro.value = e.message;
  }
}

function processarTemplate() {
  const d = props.aniversariante;
  const subs = {
    nome_aluno: props.aniversariante.tipo === 'Responsável'
      ? props.aniversariante.alunos_aniversariantes
      : props.aniversariante.nome,
    nome_responsavel:d.nome,
    nome_funcionario:d.nome,
    idade:           d.idade,
    dia_aniversario: d.dia_aniversario,
    email:           d.email,
    celular:         d.celular,
    nome_escola:     empresa.value.nome,   // agora disponível
    nome_fantasia:   empresa.value.nome_fantasia,
  };
  templateProcessado.value = renderHtmlTemplate(templateRaw.value, subs);
}

async function enviarEmail() {
  // evita reentrância
  if (isSending.value) return;
  isSending.value = true;
  erro.value = '';

  // 1) Obtém o nome do usuário a partir do globalState
  const enviadoPor = userName;

  // 2) Monta o payload incluindo `por`
  const payload = {
    pessoa_fisica_id:   props.aniversariante.id,
    destinatario_email: props.aniversariante.email,
    destinatario_nome:  props.aniversariante.nome,
    tipo:               props.aniversariante.tipo,
    template:           documentoTitulo.value,
    assunto:            modalTitle.value,
    corpo:              templateProcessado.value,
    por:                enviadoPor    // ← aqui
  };

  // 3) Chama o endpoint
  await axios.post(
    `/operacional/emails/enviar-aniversario`,
    payload
  );

  emit('sent');
  close();
  isSending.value = false;
}

function close() {
  emit('close');
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;           /* limita ao viewport */
  display: flex;
  flex-direction: column;     /* header / body / footer */
  border-radius: 0;
  overflow: hidden;           /* remove scroll externo */
}

.modal-header {
  background: #f5f5f5;
  padding: .75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-body {
  flex: 1 1 auto;             /* ocupa espaço restante */
  min-height: 0;              /* ESSENCIAL para scroll funcionar */
  overflow-y: auto;           /* scroll apenas aqui */
  padding: 1rem;
}

.modal-body .ql-editor {
  min-height: 200px;
}

.modal-body img {
  max-width: 100%;
  display: block;
  margin-top: 1rem;
}

.alert {
  margin-top: 8px;
  color: red;
}

.modal-footer {
  background: #f5f5f5;
  padding: .75rem 1rem;
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  flex-shrink: 0;
}

.btn {
  min-width: 70px;
  padding: 6px 15px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.required { color: red; }
</style>
